import { NgClass, NgStyle } from '@angular/common';
import { Component, effect, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNav,
  IonNavLink,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  ModalController,
  NavParams,
} from '@ionic/angular/standalone';

import { ActivatedRoute, Router } from '@angular/router';
import {
  AttributesPickerStore,
  AttributesPickerStoreType,
} from '@apps/mtjr/vendors/app/product/attributes/attributes.store';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mav-attributes-modal',
  imports: [IonNav],
  providers: [AttributesPickerStore],
  template: `
    <ion-nav></ion-nav>
  `,
})
export class AttributesModalComponent implements OnDestroy {
  static modalId = 'attrm';
  attributesStore: AttributesPickerStoreType = inject(NavParams).get('attributesStore');
  ionNav = viewChild(IonNav);
  router = inject(Router);
  route = inject(ActivatedRoute);
  modalCtrl = inject(ModalController);
  routeSub?: Subscription;
  navSub?: Subscription;

  constructor() {
    effect(async () => {
      const nav = this.ionNav();
      if (!nav) return;
      if (!nav.root) {
        await nav.setRoot(AttributesSheetComponent, { attributesStore: this.attributesStore });
      }
      this.navSub = nav.ionNavDidChange.subscribe(async () => {
        const active = await nav.getActive();
        const tagName = active?.element?.tagName?.toLowerCase();
        if (tagName) {
          this.router.navigate([], {
            queryParams: { attrm: tagName },
            queryParamsHandling: 'merge',
            relativeTo: this.route,
          });
        }
      });
    });

    this.routeSub = this.route.queryParams.subscribe(async (params) => {
      const nav = this.ionNav();
      if (!nav) return;
      const attrm = params[AttributesModalComponent.modalId];
      const canGoBack = await nav.canGoBack();
      if (!attrm) {
        this.modalCtrl.dismiss();
      } else {
        if (attrm === 'mav-attributes' && canGoBack) {
          await nav.pop();
        } else if (attrm === 'mav-attribute-details' && !canGoBack) {
          await nav.push(AttributesDetailsSheetComponent, {
            attributesStore: this.attributesStore,
            attribute: this.attributesStore.lastSelectedAttribute(),
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.attributesStore.updateFilterText('');
    this.router.navigate([], {
      queryParams: { attrm: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }
}

@Component({
  selector: 'mav-attributes',
  imports: [
    IonButton,
    IonItem,
    IonLabel,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonContent,
    IonSearchbar,
    IonNavLink,
    TranslatePipe,
    NgClass,
    NgStyle,
    IonIcon,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button data-cy="close-button" (click)="modalCtrl.dismiss()">
            {{ 'COMMON.CLOSE' | translate }}
          </ion-button>
        </ion-buttons>
        <ion-title>{{ 'PRODUCTS.SELECT_ATTRIBUTES' | translate }}</ion-title>
      </ion-toolbar>
      <ion-searchbar
        mode="ios"
        data-cy="attr-searchbar"
        [placeholder]="'COMMON.SEARCH' | translate"
        (ionInput)="updateQuery($event)"
      />
    </ion-header>
    <ion-content (pointerdown)="$event.preventDefault(); $event.stopPropagation()">
      @for (attribute of attributesStore.filteredAttributes(); track $index) {
        <ion-nav-link
          router-direction="forward"
          [component]="component"
          [componentProps]="{ attribute, attributesStore }"
        >
          <ion-item detail="true" class="cursor-pointer hover:bg-medium/10">
            <ion-label data-cy="attr-label">
              {{ 'ATTRIBUTE.' + attribute.label.toUpperCase() | translate }}
            </ion-label>
          </ion-item>
        </ion-nav-link>
        <div>
          @if (attributesStore.innerValuesSearchPreview(); as valuePreview) {
            @for (value of valuePreview[attribute.key]; track $index) {
              @if (attribute.type === 'color') {
                <ion-item class="cursor-pointer hover:bg-medium/10">
                  <ion-icon slot="start" name="search" size="small" color="medium"></ion-icon>
                  @if (value.subKeys) {
                    <div class="flex items-center flex-wrap gap-2 px-1 py-2">
                      @for (subKey of value.subKeys; track $index) {
                        <div
                          data-cy="attr-color-key"
                          (click)="attributesStore.toggleAttribute(attribute, value, subKey)"
                          class="flex size-10 items-center justify-center rounded-2xl border border-solid border-gray-200"
                          [ngClass]="{
                            'border-primary border-2 opacity-90': attributesStore.selectedKeys().includes(subKey),
                          }"
                          [ngStyle]="{ 'background-color': subKey }"
                        >
                          @if (attributesStore.selectedKeys().includes(subKey)) {
                            <ion-icon name="checkmark-circle-outline" size="large" class="text-gray-500"></ion-icon>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <div
                      (click)="attributesStore.toggleAttribute(attribute, value)"
                      class="size-10 rounded-2xl border border-solid border-gray-200"
                      [ngClass]="{
                        'border-primary border-2 opacity-90': attributesStore.selectedKeys().includes(value.key),
                      }"
                      [ngStyle]="{ 'background-color': value.key }"
                    ></div>
                  }
                </ion-item>
              } @else {
                <ion-item
                  class="cursor-pointer hover:bg-medium/10"
                  (click)="attributesStore.toggleAttribute(attribute, value)"
                >
                  <ion-icon slot="start" name="search" size="small" color="medium"></ion-icon>
                  <ion-label data-cy="attr-key">{{ value.label }}</ion-label>
                  @if (attributesStore.selectedKeys().includes(value.key)) {
                    <ion-icon
                      data-cy="attr-checkmark"
                      slot="end"
                      color="primary"
                      name="checkmark-circle-outline"
                    ></ion-icon>
                  }
                </ion-item>
              }
            }
          }
        </div>
      }
    </ion-content>
  `,
})
export class AttributesSheetComponent {
  filteredOptions = signal<Entity.Category[]>([]);
  inputEl = viewChild<ElementRef<HTMLInputElement>>('input');
  modalCtrl = inject(ModalController);
  component = AttributesDetailsSheetComponent;
  attributesStore: AttributesPickerStoreType = inject(NavParams).get('attributesStore');

  constructor() {
    this.attributesStore.fetchAttributesList();
  }

  updateQuery(event: CustomEvent) {
    const query = event.detail.value;
    this.attributesStore.updateFilterText(query);
  }
}

@Component({
  selector: 'mav-attribute-details',
  imports: [
    IonContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonItem,
    IonLabel,
    IonBackButton,
    NgClass,
    NgStyle,
    IonList,
    TranslatePipe,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button data-cy="back-button" [text]="'COMMON.BACK' | translate"></ion-back-button>
        </ion-buttons>
        <ion-title>{{ 'PRODUCTS.ATTRIBUTES_LIST' | translate }}</ion-title>
        <ion-buttons slot="end">
          <ion-button fill="clear" data-cy="save-button" (click)="this.modalCtr.dismiss()">
            {{ 'COMMON.SAVE' | translate }}
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content (pointerdown)="$event.preventDefault(); $event.stopPropagation()">
      @if (attribute.type === 'color') {
        <ion-list>
          @for (value of attribute.values; track $index) {
            <div class="bg-surface mb-1 rounded-md px-2 py-2">
              @if (value.subKeys) {
                <div class="flex items-center justify-center flex-wrap gap-2 px-1">
                  @for (subKey of value.subKeys; track $index) {
                    <div
                      data-cy="attr-color-key"
                      (click)="attributesStore.toggleAttribute(attribute, value, subKey)"
                      class="flex size-10 items-center justify-center rounded-2xl border border-solid border-gray-200 cursor-pointer hover:border-dark hover:border-2"
                      [ngClass]="{
                        'border-primary border-2 opacity-90': attributesStore.selectedKeys().includes(subKey),
                      }"
                      [ngStyle]="{ 'background-color': subKey }"
                    >
                      @if (attributesStore.selectedKeys().includes(subKey)) {
                        <ion-icon name="checkmark-circle-outline" size="large" class="text-gray-500"></ion-icon>
                      }
                    </div>
                  }
                </div>
              } @else {
                <div
                  (click)="attributesStore.toggleAttribute(attribute, value)"
                  class="size-10 rounded-2xl border border-solid border-gray-200 cursor-pointer hover:bg-medium/10"
                  [ngClass]="{
                    'border-primary border-2 opacity-90': attributesStore.selectedKeys().includes(value.key),
                  }"
                  [ngStyle]="{ 'background-color': value.key }"
                ></div>
              }
            </div>
          }
        </ion-list>
      } @else {
        @for (value of attribute.values; track $index) {
          <ion-item
            class="cursor-pointer hover:bg-medium/10"
            (click)="attributesStore.toggleAttribute(attribute, value)"
          >
            <ion-label data-cy="attr-key" class="select-none">{{ value.label }}</ion-label>
            @if (attributesStore.selectedKeys().includes(value.key)) {
              <ion-icon data-cy="attr-checkmark" slot="end" color="primary" name="checkmark-circle-outline"></ion-icon>
            }
          </ion-item>
        }
      }
    </ion-content>
  `,
})
export class AttributesDetailsSheetComponent {
  navParams = inject(NavParams);
  attributesStore: AttributesPickerStoreType = this.navParams.get('attributesStore');
  attribute: Dtos.Attribute = this.navParams.get('attribute');
  modalCtr = inject(ModalController);

  constructor() {
    this.attributesStore.setLastSelectedAttribute(this.attribute);
  }
}
